import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, { params });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

async function deleteJSON(path) {
  try {
    const res = await api.delete(path);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

/* ---------- Helper ---------- */
const fmt = (v) => (v == null ? "0.00" : Number(v).toFixed(2));

/* ---------- Page ---------- */
export default function ViewSales() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });
  const [detailRow, setDetailRow] = React.useState(null);

  const loadSales = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await getJSON("/api/sales", { _: Date.now() });
    setLoading(false);
    if (error) {
      setToast({ open: true, msg: "Failed to load sales", sev: "error" });
      setRows([]);
      return;
    }
    setRows(Array.isArray(data) ? data : []);
  }, []);

  React.useEffect(() => {
    loadSales();
  }, [loadSales]);

  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      valueGetter: (p) => p.api.getRowIndexRelativeToVisibleRows(p.id) + 1,
    },
    {
      field: "user",
      headerName: "Sales Person",
      flex: 1,
      valueGetter: (p) => p.row.User?.fullName || "—",
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      valueGetter: (p) => p.row.Customer?.name || "—",
    },
    {
      field: "warehouse",
      headerName: "Warehouse",
      flex: 1,
      valueGetter: (p) => p.row.Warehouse?.name || "—",
    },
    {
      field: "totalTaxedPrice",
      headerName: "Total Price",
      width: 140,
      valueGetter: (p) => fmt(p.row.totalTaxedPrice),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Details">
            <IconButton size="small" onClick={() => setDetailRow(row)}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => window.location.assign(`/sales/edit/${row.id}`)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <Box sx={{ position: "relative" }}>
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
                disabled={deleting && deleteRow?.id === row.id}
              >
                <DeleteOutlineIcon />
              </IconButton>

              {deleting && deleteRow?.id === row.id && (
                <CircularProgress
                  size={26}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    mt: "-13px",
                    ml: "-13px",
                  }}
                />
              )}
            </Box>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  return (
    <>
      <Box m="20px">
        <Header
          title="SALES"
          subtitle="View sales and click details for more info"
        />
     

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Sale</DialogTitle>

        <DialogContent dividers>
          <Typography>Are you sure you want to delete this sale?</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={deleting}
            onClick={async () => {
              setDeleting(true);

              const { error } = await deleteJSON(`/api/sales/${deleteRow.id}`);

              if (error) {
                setToast({
                  open: true,
                  msg: "Failed to delete sale",
                  sev: "error",
                });
                setDeleting(false);
                return;
              }

              setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
              setToast({ open: true, msg: "Sale deleted", sev: "success" });

              setDeleting(false);
              setDeleteRow(null);
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      {detailRow && (
        <Dialog
          open={Boolean(detailRow)}
          onClose={() => setDetailRow(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Sale Details</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {/* Main Sale Details */}
              <Typography>
                <b>Sales Person:</b> {detailRow.User?.fullName || "—"}
              </Typography>
              <Typography>
                <b>Customer:</b> {detailRow.Customer?.name || "—"}
              </Typography>
              <Typography>
                <b>Warehouse:</b> {detailRow.Warehouse?.name || "—"}
              </Typography>
              <Typography>
                <b>Bank:</b> {detailRow.bank || "—"}
              </Typography>
              <Typography>
                <b>Credit Sale:</b> {detailRow.credit ? "Yes" : "No"}
              </Typography>
              <Typography>
                <b>Sales Date:</b> {detailRow.salesDate?.slice(0, 10) || "—"}
              </Typography>
              <Typography>
                <b>TIN No:</b> {detailRow.tinNo || "—"}
              </Typography>
              <Typography>
                <b>FS No:</b> {detailRow.fsNo || "—"}
              </Typography>
              <Typography>
                <b>Machine No:</b> {detailRow.machineNo || "—"}
              </Typography>
              <Typography>
                <b>Description:</b> {detailRow.description || "—"}
              </Typography>
              <Typography>
                <b>Paid Amount:</b>{" "}
                {Number(detailRow.paidAmount || 0).toFixed(2)}
              </Typography>
              <Typography>
                <b>Total Price:</b>{" "}
                {Number(detailRow.totalTaxedPrice || 0).toFixed(2)}
              </Typography>
              <Typography>
                <b>Withholding Amount:</b>{" "}
                {Number(detailRow.withholdingAmount || 0).toFixed(2)}
              </Typography>
              <Typography>
                <b>Vat:</b> {Number(detailRow.vat || 0).toFixed(2)}
              </Typography>
              {/* Sales Items */}
              {Array.isArray(detailRow.SalesItems) &&
                detailRow.SalesItems.length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1">
                      <b>Items</b>
                    </Typography>
                    <Box
                      display="grid"
                      gridTemplateColumns="1fr 1fr"
                      gap={2}
                      mt={1}
                    >
                      {detailRow.SalesItems.map((item) => (
                        <Box
                          key={item.id}
                          p={1}
                          border="1px solid #ddd"
                          borderRadius={1}
                          display="flex"
                          flexDirection="column"
                          gap={0.5}
                        >
                          <Typography>
                            <b>Name:</b> {item.Item?.name || "—"}
                          </Typography>
                          <Typography>
                            <b>Qty:</b> {Number(item.quantity || 0).toFixed(2)}
                          </Typography>
                          <Typography>
                            <b>Unit Price:</b>{" "}
                            {Number(item.unitPrice || 0).toFixed(2)}
                          </Typography>
                          <Typography>
                            <b>Total:</b>{" "}
                            {Number(item.totalPrice || 0).toFixed(2)}
                          </Typography>
                          <Typography>
                            <b>Bonus Units:</b>{" "}
                            {Number(item.bonus || 0).toFixed(2)}
                          </Typography>
                          <Typography>
                            <b>Bonus Amount:</b>{" "}
                            {Number(item.bonusAmount || 0).toFixed(2)}
                          </Typography>
                          <Typography>
                            <b>Sponsor Units:</b>{" "}
                            {Number(item.sponsor || 0).toFixed(2)}
                          </Typography>
                          <Typography>
                            <b>Sponsor Amount:</b>{" "}
                            {Number(item.sponsorAmount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailRow(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- Subcomponent ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}


